<script setup lang="ts">
/**
 * Company verification (KYC) for business accounts.
 *
 * Every user of the company can see where verification stands. Only admins
 * upload, replace or open documents, and the server enforces that: the
 * controls are hidden here for non-admins so nobody is offered a button that
 * would be refused.
 *
 * Which documents can be uploaded mirrors the server's rule
 * (companyKycModel.assertUploadAllowed), again so that a button is only shown
 * when pressing it can work:
 *   pending       any document
 *   under_review  only a required document that is still missing
 *   rejected      anything not already accepted
 *   approved / exempt / personal  nothing
 */
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import axios from "axios";
import { ElMessage } from "element-plus";
import { isCompanyAdminFromToken } from "../../utils/authRole";
import {
  useCompanyKyc,
  kycUrl,
  authHeaders,
  reasonFrom,
  ACTION_JOURNEYS,
  canUploadType,
  awaitingLabel,
  docsPendingVerification,
  type KycDocumentType,
  type KycDocument,
  type Tone,
} from "../../composables/useCompanyKyc";


const kyc = useCompanyKyc();
const view = kyc.view;
const loading = ref(true);
onMounted(async () => {
  await kyc.load(true);
  loading.value = false;
});

const isAdmin = computed(() => isCompanyAdminFromToken(localStorage.getItem("_panda")));
const company = computed(() => view.value?.company || null);
const status = computed(() => company.value?.kyc_status || null);
const isBusiness = computed(() => company.value?.account_type === "business");
const types = computed(() => view.value?.document_types || []);
const required = computed(() => types.value.filter((t) => t.is_required));
const optional = computed(() => types.value.filter((t) => !t.is_required));

const isLive = (t: KycDocumentType) => !!t.document && t.document.review_status !== "rejected";
const missingRequired = computed(() => required.value.filter((t) => !t.document));
const complete = computed(() => required.value.every(isLive));

const canUpload = (t: KycDocumentType) => isAdmin.value && canUploadType(view.value, t);

// Upload and auto-submit happen together on the server when the last missing
// required document arrives with nothing rejected. The button covers the
// remaining case: every required document is in, but an optional one was
// rejected and the customer chooses not to replace it.
const canSubmit = computed(
  () =>
    isAdmin.value &&
    isBusiness.value &&
    (status.value === "pending" || status.value === "rejected") &&
    complete.value,
);

// ── Status banner ────────────────────────────────────────
const fmtDate = (d?: string | null) => {
  if (!d) return "";
  const date = new Date(d);
  return isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" });
};

const banner = computed<{ tone: Tone; title: string; body: string }>(() => {
  const c = company.value;
  const adminLine = isAdmin.value ? "" : " An admin of your account uploads them.";
  if (!c) return { tone: "muted", title: "", body: "" };
  if (c.account_type !== "business" || c.kyc_status === "not_required") {
    return {
      tone: "muted",
      title: "Verification does not apply to personal accounts",
      body: "Company documents are only needed for business accounts.",
    };
  }
  switch (c.kyc_status) {
    case "pending":
      return {
        tone: "warn",
        title: "Upload your company documents",
        body: `We need the ${required.value.length} required documents below to verify ${c.company_name}. Once they are all in, we review them and email your admins.${adminLine}`,
      };
    case "under_review":
      return {
        tone: "info",
        title: "We're reviewing your documents",
        body: missingRequired.value.length
          ? `Submitted ${fmtDate(c.kyc_submitted_at)}. One more document is now required: upload it below so the review can finish.${adminLine}`
          : `Submitted ${fmtDate(c.kyc_submitted_at)}. We'll email your admins when the review is done.`,
      };
    case "rejected":
      if (view.value?.journey === "expired") {
        return {
          tone: "bad",
          title: "A document has expired",
          body: `Upload a current copy of anything marked Expired to keep your company verified.${adminLine}`,
        };
      }
      return {
        tone: "bad",
        title: "Some documents need replacing",
        body: `Replace the documents marked Not accepted${missingRequired.value.length ? " and upload any marked Not uploaded" : ""}, and we'll review them again.${adminLine}`,
      };
    case "approved": {
      const waiting = docsPendingVerification(view.value);
      const verified = c.kyc_reviewed_at ? `Verified on ${fmtDate(c.kyc_reviewed_at)}.` : "Your documents have been accepted.";
      return {
        tone: "ok",
        title: "Your company is verified",
        body: waiting.length
          ? `${verified} ${waiting.map((t) => t.label).join(" and ")} ${waiting.length > 1 ? "are" : "is"} pending verification by Xobo.`
          : verified,
      };
    }
    default:
      return {
        tone: "muted",
        title: "No documents needed",
        body: "Your account was set up before company verification started, so there is nothing to upload. If we ever need documents from you, we'll email your admins.",
      };
  }
});

// One segment per required document, coloured by where it stands.
const segmentTone = (t: KycDocumentType): Tone => {
  const d = t.document;
  if (!d) return "muted";
  if (d.review_status === "approved") return "ok";
  if (d.review_status === "rejected") return "bad";
  return "info";
};
const progressText = computed(() => {
  const p = view.value?.progress;
  if (!p) return "";
  // Once staff have reviewed, "accepted" is the count that matters: a rejected
  // document is uploaded but still has to be replaced.
  if (status.value === "approved" || status.value === "rejected") {
    return `${p.approved} of ${p.required} required documents accepted`;
  }
  return `${p.uploaded} of ${p.required} required documents uploaded`;
});
const showProgress = computed(
  () => isBusiness.value && ["pending", "under_review", "rejected", "approved"].includes(status.value || ""),
);

// ── Document rows ────────────────────────────────────────
const docState = (t: KycDocumentType): { label: string; tone: Tone } => {
  const d = t.document;
  if (!d) {
    const owed = t.is_required && ["under_review", "rejected"].includes(status.value || "");
    return owed ? { label: "Not uploaded", tone: "warn" } : { label: "Not uploaded", tone: "muted" };
  }
  if (d.expired || d.rejection_reason_code === "doc_expired") return { label: "Expired", tone: "bad" };
  if (d.review_status === "approved") return { label: d.expiring_soon ? "Expires soon" : "Accepted", tone: d.expiring_soon ? "warn" : "ok" };
  if (d.review_status === "rejected") return { label: "Not accepted", tone: "bad" };
  return { label: awaitingLabel(status.value, t.is_required), tone: "info" };
};

const fmtBytes = (n: number) =>
  n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;

const docMeta = (d: KycDocument) =>
  [d.original_filename, fmtBytes(d.bytes), `uploaded ${fmtDate(d.uploaded_at)}`, d.uploaded_by_name ? `by ${d.uploaded_by_name}` : ""]
    .filter(Boolean)
    .join(" · ");

// ── Upload ───────────────────────────────────────────────
// Uploads happen in the guided flow (/verify), which explains each document
// and asks for the number and expiry its type needs. Upload / Replace here
// open that document's step directly.
const router = useRouter();
const startGuided = (t?: KycDocumentType) =>
  router.push(t ? { path: "/verify", query: { doc: t.doc_type } } : { path: "/verify" });
// Number and expiry, when the type asks for them. An expiry the reason box
// already explains ("Expired on …") is not repeated.
const factsOf = (d: KycDocument) =>
  [
    d.document_number ? `No. ${d.document_number}` : "",
    d.expires_at && d.rejection_reason_code !== "doc_expired"
      ? `${d.expired ? "Expired" : "Valid until"} ${fmtDay(d.expires_at)}`
      : "",
  ]
    .filter(Boolean)
    .join(" · ");
const actionJourney = computed(() => !!view.value && ACTION_JOURNEYS.includes(view.value.journey));
const fmtDay = (ymd: string | null) =>
  ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" }) : "";

// ── View a document ──────────────────────────────────────
const opening = ref<number | null>(null);
const openDoc = async (d: KycDocument) => {
  // Open the tab inside the click so popup blockers allow it, then point it
  // at the short-lived link once the server has issued one.
  const tab = window.open("", "_blank");
  opening.value = d.document_id;
  try {
    const res = await axios.get(kycUrl(`/documents/${d.document_id}/url`), { headers: authHeaders() });
    const url = res.data?.url;
    if (!url) throw new Error("no url");
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.location.assign(url);
    }
  } catch (err) {
    tab?.close();
    ElMessage({ message: reasonFrom(err, "Could not open the document. Please try again."), type: "error" });
  } finally {
    opening.value = null;
  }
};

// ── Submit ───────────────────────────────────────────────
const submitting = ref(false);
const submit = async () => {
  submitting.value = true;
  try {
    const res = await axios.post(kycUrl("/submit"), {}, { headers: authHeaders() });
    kyc.adopt(res.data?.data || null);
    ElMessage({ message: "Submitted. We'll email your admins when the review is done.", type: "success" });
  } catch (err) {
    ElMessage({ message: reasonFrom(err, "Could not submit. Please try again."), type: "error" });
    void kyc.load(true);
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <div class="ac-card" v-loading="loading && !view">
    <div class="ac-panel-head">
      <div>
        <h2 class="ac-panel-title">Company verification</h2>
        <p class="ac-panel-desc">
          The registration documents we check before verifying a business account
        </p>
      </div>
    </div>

    <div v-if="kyc.error.value && !view" class="ac-card-body">
      <div class="ac-error">{{ kyc.error.value }}</div>
      <button class="ac-btn ac-btn--ghost ac-btn--sm" @click="kyc.load(true)">Try again</button>
    </div>

    <div v-else-if="view" class="ac-card-body">
      <!-- Where verification stands -->
      <section class="kv-banner" :class="`kv-banner--${banner.tone}`">
        <span class="kv-banner-icon" aria-hidden="true">
          <svg v-if="banner.tone === 'ok'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>
          <svg v-else-if="banner.tone === 'info'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
          <svg v-else-if="banner.tone === 'bad'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
          <svg v-else-if="banner.tone === 'warn'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
        </span>
        <div class="kv-banner-text">
          <h3 class="kv-banner-title">{{ banner.title }}</h3>
          <p class="kv-banner-body">{{ banner.body }}</p>
          <p v-if="view.order_gate?.blocks_orders" class="kv-banner-body kv-banner-gate">
            You can place orders once your company is verified.
          </p>
          <blockquote v-if="status === 'rejected' && company?.kyc_rejection_note" class="kv-banner-note">
            <span>Note from Xobo</span>
            {{ company.kyc_rejection_note }}
          </blockquote>
          <button v-if="isAdmin && actionJourney" type="button" class="ac-btn ac-btn--sm kv-banner-cta" @click="startGuided()">
            {{ view.journey === "replace" || view.journey === "expired" ? "Replace documents step by step" : "Upload step by step" }}
          </button>
        </div>
      </section>

      <div v-if="showProgress" class="kv-progress">
        <div class="kv-segments" role="img" :aria-label="progressText">
          <span
            v-for="t in required"
            :key="t.doc_type"
            class="kv-segment"
            :class="`kv-segment--${segmentTone(t)}`"
          ></span>
        </div>
        <span class="kv-progress-text">{{ progressText }}</span>
      </div>

      <!-- What staff check the documents against -->
      <dl v-if="isBusiness" class="ac-facts kv-facts">
        <div class="ac-fact">
          <dt>Company</dt>
          <dd>{{ company?.company_name }}</dd>
        </div>
        <div class="ac-fact">
          <dt>Registration number</dt>
          <dd>{{ company?.registration_number || "Not provided" }}</dd>
        </div>
        <div class="ac-fact">
          <dt>KRA PIN</dt>
          <dd>{{ company?.kra_pin || "Not provided" }}</dd>
        </div>
      </dl>

      <template v-if="showProgress && types.length">
        <template v-for="group in [{ key: 'required', title: 'Required documents', list: required }, { key: 'optional', title: 'Optional documents', list: optional }]" :key="group.key">
          <section v-if="group.list.length" class="kv-group">
            <div class="ac-section-label kv-group-label">{{ group.title }}</div>
            <ul class="kv-list">
              <li v-for="t in group.list" :key="t.doc_type" class="kv-row" :class="{ 'is-rejected': t.document?.review_status === 'rejected' }">
                <span class="kv-file-icon" :class="`kv-file-icon--${docState(t).tone}`" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                </span>

                <div class="kv-row-main">
                  <div class="kv-row-title">
                    <span class="kv-row-label">{{ t.label }}</span>
                    <span class="ac-pill" :class="`ac-pill--${docState(t).tone}`">{{ docState(t).label }}</span>
                  </div>
                  <div v-if="t.document" class="kv-row-meta">{{ docMeta(t.document) }}</div>
                  <div v-if="t.document && factsOf(t.document)" class="kv-row-meta">{{ factsOf(t.document) }}</div>
                  <div v-if="t.document?.review_status === 'rejected'" class="kv-reason">
                    <strong>{{ t.document.rejection_reason_label || "Not accepted" }}</strong>
                    <span v-if="t.document.rejection_note">{{ t.document.rejection_note }}</span>
                  </div>
                </div>

                <div class="kv-row-actions">
                  <button
                    v-if="t.document && isAdmin"
                    class="ac-btn ac-btn--ghost ac-btn--sm"
                    :disabled="opening === t.document.document_id"
                    @click="openDoc(t.document)"
                  >
                    {{ opening === t.document.document_id ? "Opening…" : "View" }}
                  </button>
                  <button
                    v-if="canUpload(t)"
                    class="ac-btn ac-btn--sm"
                    :class="{ 'ac-btn--ghost': t.document && t.document.review_status !== 'rejected' }"
                    @click="startGuided(t)"
                  >
                    {{ t.document ? "Replace" : "Upload" }}
                  </button>
                </div>
              </li>
            </ul>
          </section>
        </template>
      </template>

      <div v-if="isBusiness && view.can_upload" class="ac-actions">
        <p class="ac-note">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
          <span v-if="isAdmin">PDF, JPG or PNG, up to 10 MB each. Documents are stored privately and only your admins and our verification team can open them.</span>
          <span v-else>Only admins of your account can upload or open documents.</span>
        </p>
        <button v-if="canSubmit" class="ac-btn" :disabled="submitting" @click="submit">
          {{ submitting ? "Submitting…" : "Submit for review" }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.kv-banner {
  display: flex;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1.5px solid;
}
.kv-banner--warn { background: #fffbeb; border-color: #fde68a; --kv-ink: #92400e; }
.kv-banner--info { background: #eff6ff; border-color: #bfdbfe; --kv-ink: #1e40af; }
.kv-banner--bad { background: #fef2f2; border-color: #fecaca; --kv-ink: #991b1b; }
.kv-banner--ok { background: #f0fdf4; border-color: #bbf7d0; --kv-ink: #166534; }
.kv-banner--muted { background: #f8fafc; border-color: #e2e8f0; --kv-ink: #334155; }
.kv-banner-icon {
  display: flex;
  flex-shrink: 0;
  color: var(--kv-ink);
  padding-top: 1px;
}
.kv-banner-text {
  min-width: 0;
}
.kv-banner-title {
  margin: 0 0 3px;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--kv-ink);
}
.kv-banner-body {
  margin: 0;
  font-size: 0.83rem;
  line-height: 1.5;
  color: #334155;
  max-width: 62ch;
}
.kv-banner-gate {
  margin-top: 6px;
  font-weight: 600;
}
.kv-banner-cta { margin-top: 12px; }
.kv-banner-note {
  margin: 10px 0 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
  font-size: 0.82rem;
  color: #334155;
}
.kv-banner-note span {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--kv-ink);
  margin-bottom: 2px;
}

.kv-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
}
.kv-segments {
  display: flex;
  gap: 4px;
  flex: 0 1 260px;
}
.kv-segment {
  flex: 1;
  height: 6px;
  border-radius: 3px;
}
.kv-segment--muted { background: #e2e8f0; }
.kv-segment--info { background: #60a5fa; }
.kv-segment--ok { background: #22c55e; }
.kv-segment--bad { background: #ef4444; }
.kv-progress-text {
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}

.kv-facts {
  margin: 22px 0 26px;
}

.kv-group + .kv-group {
  margin-top: 26px;
}
.kv-group-label {
  margin-top: 0;
  margin-bottom: 4px;
}

.kv-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.kv-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #f1f5f9;
}
.kv-row:last-child {
  border-bottom: none;
}
.kv-file-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kv-file-icon--muted { background: #f1f5f9; color: #94a3b8; }
.kv-file-icon--warn { background: #fffbeb; color: #b45309; }
.kv-file-icon--info { background: #eff6ff; color: #2563eb; }
.kv-file-icon--ok { background: #f0fdf4; color: #16a34a; }
.kv-file-icon--bad { background: #fef2f2; color: #dc2626; }

.kv-row-main {
  flex: 1;
  min-width: 0;
}
.kv-row-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.kv-row-label {
  font-size: 0.88rem;
  font-weight: 600;
  color: #12284a;
}
.kv-row-meta {
  margin-top: 3px;
  font-size: 0.77rem;
  color: #94a3b8;
  overflow-wrap: anywhere;
}
.kv-reason {
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: #fef2f2;
  font-size: 0.8rem;
  color: #7f1d1d;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.kv-reason strong {
  font-weight: 600;
  color: #b91c1c;
}

.kv-row-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  padding-top: 1px;
}

@media (max-width: 640px) {
  .kv-row {
    flex-wrap: wrap;
  }
  .kv-row-actions {
    width: 100%;
    padding-left: 50px;
  }
}
</style>
